<template>
  <div class="space-y-6" data-test-id="merchant-transaction-search-form">
    <div
      class="bg-surface-0 dark:bg-surface-900 border border-surface-200 dark:border-surface-700 rounded-lg p-4"
    >
      <div class="grid grid-cols-1 gap-4 max-w-screen-lg mx-auto">
    <!-- Merchant Selection (optional) -->
    <div v-if="showMerchantSelect" class="flex flex-col gap-2 w-full">
      <label class="text-sm font-medium text-surface-900 dark:text-surface-0">
        Merchant <span class="text-red-500">*</span>
      </label>
      <MerchantAutocomplete
        v-model="selectedMerchantId"
        data-test-id="merchant-select"
      />
    </div>

    <!-- Filter Builder -->
    <div class="flex flex-col gap-2">
      <div class="flex items-center justify-between">
        <label class="text-sm font-medium text-surface-900 dark:text-surface-0">
          Search Filters
        </label>
      </div>
      <p class="text-xs text-surface-500 dark:text-surface-400">
        Add filters to search by specific fields. Filters are combined with AND
        logic.
      </p>
    </div>

    <!-- Filter Value Inputs -->
    <div
      v-for="(filterType, index) in selectedFilterTypes"
      :key="`filter-${filterType}-${index}`"
      class="flex flex-col gap-2"
    >
      <!-- Filter Row: Input Field + Filter Select Dropdown -->
      <div class="flex items-end gap-2">
        <div class="flex-[2] min-w-0">
          <InputText
            :id="`filter-value-input-${filterType}`"
            v-model="filterValues[filterType]"
            :placeholder="getFilterTypeLabel(filterType)"
            variant="filled"
            @keyup.enter="handleSubmit"
            class="w-full"
            :data-test-id="`filter-value-input-${filterType}`"
            size="small"
          />
        </div>
        <div class="w-1/3 flex-shrink-0">
          <Select
            :model-value="filterType"
            :options="getAvailableFilterOptionsForIndex(index)"
            optionLabel="label"
            optionValue="value"
            @update:model-value="updateFilterType(index, $event)"
            class="w-full filter-type-select"
            :data-test-id="`filter-type-select-${index}`"
            size="small"
          />
        </div>
        <Button
          v-if="selectedFilterTypes.length > 1"
          icon="pi pi-times"
          variant="text"
          severity="secondary"
          size="small"
          @click="removeFilter(filterType)"
          :aria-label="`Remove ${getFilterTypeLabel(filterType)} filter`"
          :data-test-id="`remove-filter-${filterType}`"
          class="flex-shrink-0"
        />
      </div>
      <!-- Add Filter Button (equal width to row above) -->
      <Button
        v-if="
          index === selectedFilterTypes.length - 1 &&
          availableFilterOptions.length > 0
        "
        type="button"
        label="Add Filter"
        icon="pi pi-plus"
        variant="outlined"
        size="small"
        :disabled="availableFilterOptions.length === 0"
        @click="addNewFilter"
        data-test-id="add-filter-btn"
        class="w-full"
      />
    </div>
    <!-- Initial Add Filter Button (when no filters exist) -->
    <Button
      v-if="selectedFilterTypes.length === 0"
      type="button"
      label="Add Filter"
      icon="pi pi-plus"
      variant="outlined"
      size="small"
      :disabled="availableFilterOptions.length === 0"
      @click="addNewFilter"
      data-test-id="add-filter-btn-initial"
      class="w-full"
    />

    <!-- Date Range Picker -->
    <div>
      <DateRangePicker />
      <p class="text-xs text-surface-600 dark:text-surface-300 mt-2">
        <slot name="date-range-help-text">
          Leave blank to browse all transactions, or set a date range to narrow
          results.
        </slot>
      </p>
    </div>

    <!-- Action buttons slot -->
    <slot
      name="actions"
      :can-search="canSearch"
      :submit-search="handleSubmit"
      :submit-button-label="submitButtonLabel"
    >
      <div class="flex justify-end">
        <Button
          @click="handleSubmit"
          :disabled="!canSearch"
          :label="submitButtonLabel"
          icon="pi pi-search"
          data-test-id="submit-search-btn"
          size="small"
        />
      </div>
    </slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'

import { useSearchStore } from '@/stores/searchStore'
import { FILTER_TYPE_OPTIONS } from '@/types/taxTransaction'
import DateRangePicker from './DateRangePicker.vue'
import MerchantAutocomplete from '@/components/common/MerchantAutocomplete.vue'

interface Props {
  showMerchantSelect?: boolean
  merchantId?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  showMerchantSelect: false,
  merchantId: null,
})

const emit = defineEmits<{
  search: [query: Record<string, string>]
  'update:merchantId': [value: string | null]
}>()

const searchStore = useSearchStore()

// Filter state - initialize with first filter by default
const selectedFilterTypes = ref<string[]>([])
const filterValues = ref<Record<string, string>>({})

function initializeFiltersFromStore() {
  // Load filters from store (works for both dialog and merchant config form)
  const storeFilter = searchStore.currentFilter
  const activeFilters: string[] = []
  const values: Record<string, string> = {}

  FILTER_TYPE_OPTIONS.forEach(option => {
    const value = storeFilter[option.value as keyof typeof storeFilter]
    if (value) {
      activeFilters.push(option.value)
      values[option.value] = String(value)
    }
  })

  if (activeFilters.length > 0) {
    selectedFilterTypes.value = activeFilters
    filterValues.value = values
  } else {
    // If no filters in store, show first filter option by default
    const firstOption = FILTER_TYPE_OPTIONS[0]
    if (firstOption) {
      selectedFilterTypes.value.push(firstOption.value)
      filterValues.value[firstOption.value] = ''
    }
  }
}

// Initialize filters on mount
onMounted(() => {
  initializeFiltersFromStore()
})

// Merchant selection
const selectedMerchantId = computed<string | null>({
  get: () => {
    if (props.showMerchantSelect) {
      return searchStore.selectedMerchantId
    }
    return props.merchantId
  },
  set: value => {
    if (props.showMerchantSelect) {
      searchStore.setSelectedMerchantId(value)
    }
    emit('update:merchantId', value)
  },
})

const getFilterTypeLabel = (filterType: string): string => {
  const option = FILTER_TYPE_OPTIONS.find(o => o.value === filterType)
  return option?.label || filterType
}

// Available filter options (not yet added)
const availableFilterOptions = computed(() => {
  return FILTER_TYPE_OPTIONS.filter(
    option => !selectedFilterTypes.value.includes(option.value),
  )
})

// Get available filter options for a specific index (includes current selection)
const getAvailableFilterOptionsForIndex = (index: number) => {
  const currentFilterType = selectedFilterTypes.value[index]
  const otherSelectedTypes = selectedFilterTypes.value.filter(
    (_, i) => i !== index,
  )
  return FILTER_TYPE_OPTIONS.filter(
    option =>
      option.value === currentFilterType ||
      !otherSelectedTypes.includes(option.value),
  )
}

// Update filter type at a specific index
const updateFilterType = (index: number, newFilterType: string | null) => {
  if (!newFilterType) return

  const oldFilterType = selectedFilterTypes.value[index]

  // If changing to the same type, do nothing
  if (oldFilterType === newFilterType) return

  const oldValue = filterValues.value[oldFilterType]

  // Update the filter type at this index
  selectedFilterTypes.value[index] = newFilterType

  // Initialize new filter value if it doesn't exist
  // Only move old value if new filter doesn't have a value yet
  if (!filterValues.value[newFilterType]) {
    filterValues.value[newFilterType] = oldValue || ''
  }

  // Clean up old filter value if no longer used
  if (oldFilterType && !selectedFilterTypes.value.includes(oldFilterType)) {
    delete filterValues.value[oldFilterType]
  }
}

// Add a new filter (uses first available option)
const addNewFilter = () => {
  const firstAvailable = availableFilterOptions.value[0]
  if (firstAvailable) {
    selectedFilterTypes.value.push(firstAvailable.value)
    if (!filterValues.value[firstAvailable.value]) {
      filterValues.value[firstAvailable.value] = ''
    }
  }
}

const canSearch = computed(() => {
  const hasMerchant = props.showMerchantSelect
    ? !!selectedMerchantId.value
    : !!props.merchantId
  const hasValidDateRange = searchStore.isDateRangeValid

  // Check if any filters have values
  const hasActiveFilters = selectedFilterTypes.value.some(filterType =>
    filterValues.value[filterType]?.trim(),
  )

  // If any filter has a value, all filters must have values
  // Otherwise, allow browsing with empty filters when merchant is selected
  const allFiltersHaveValues = selectedFilterTypes.value.every(filterType =>
    filterValues.value[filterType]?.trim(),
  )

  return (
    hasMerchant &&
    hasValidDateRange &&
    (!hasActiveFilters || allFiltersHaveValues)
  )
})

const submitButtonLabel = computed(() => {
  const hasActiveFilters =
    selectedFilterTypes.value.length > 0 &&
    selectedFilterTypes.value.every(ft => filterValues.value[ft]?.trim())
  return hasActiveFilters ? 'Search' : 'Browse Transactions'
})

const removeFilter = (filterType: string) => {
  selectedFilterTypes.value = selectedFilterTypes.value.filter(
    ft => ft !== filterType,
  )
  delete filterValues.value[filterType]
}

const handleSubmit = () => {
  if (!canSearch.value) return

  const queryParams: Record<string, string> = {}

  // Add merchant ID
  const merchantId = props.showMerchantSelect
    ? selectedMerchantId.value
    : props.merchantId
  if (merchantId) {
    queryParams.merchantId = merchantId
  }

  // Add all active filters to query params
  selectedFilterTypes.value.forEach(filterType => {
    const value = filterValues.value[filterType]?.trim()
    if (value) {
      queryParams[filterType] = value
    }
  })

  // Add date range
  if (searchStore.currentDateRange.startDate) {
    queryParams.startDate = searchStore.currentDateRange.startDate
      .toISOString()
      .split('T')[0]
  }
  if (searchStore.currentDateRange.endDate) {
    queryParams.endDate = searchStore.currentDateRange.endDate
      .toISOString()
      .split('T')[0]
  }

  emit('search', queryParams)
}

// Watch for merchant prop changes (for non-modal version)
watch(
  () => props.merchantId,
  merchantId => {
    if (merchantId && !props.showMerchantSelect) {
      searchStore.setSelectedMerchantId(merchantId)
    }
  },
  { immediate: true },
)

// Initialize filters from store
function initializeFilters() {
  initializeFiltersFromStore()
}

// Watch for when filters become empty and reinitialize
watch(
  () => selectedFilterTypes.value.length,
  newLength => {
    if (newLength === 0) {
      initializeFilters()
    }
  },
)

// Expose methods for parent components
defineExpose({
  clearSearch: () => {
    selectedFilterTypes.value = []
    filterValues.value = {}
    searchStore.clearDateRange()
    if (props.showMerchantSelect) {
      searchStore.clearSelectedMerchant()
    }
  },
  initializeFilters,
})
</script>

<style scoped>
/* Truncate Select label with ellipsis */
.filter-type-select :deep(.p-select-label) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.75rem;
}

/* Ensure Select dropdown panel items also wrap */
:deep(.p-select-panel .p-select-item-label) {
  white-space: normal;
  word-wrap: break-word;
  line-height: 1.3;
}
</style>
